import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Plus,
  Edit,
  Search,
  X,
  Save,
  Receipt,
  Loader2,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { toast } from 'sonner';
import taxMasterService from '../../services/taxMasterService';

// The existing tax_master.taxType enum is GOODS/SERVICE (goods use an HSN code,
// services use a SAC code). The BRD's Intra/Inter labels map to the derived
// CGST/SGST vs IGST columns, which the backend computes from GST% automatically.
const TAX_TYPE_OPTIONS = [
  { value: 'GOODS', label: 'Goods (HSN)' },
  { value: 'SERVICE', label: 'Service (SAC)' },
];

const TAX_TYPE_LABELS = TAX_TYPE_OPTIONS.reduce(
  (acc, o) => { acc[o.value] = o.label; return acc; },
  {},
);

// The tax code is an HSN for goods and a SAC for services, so the form field
// label follows the selected type. The list column stays "HSN / SAC Code"
// because it mixes both. (A row with no/unknown type falls back to HSN.)
const codeLabelFor = (taxType) => (taxType === 'SERVICE' ? 'SAC Code' : 'HSN Code');

// GST% quick-filter buckets shown in the toolbar dropdown.
const GST_FILTER_OPTIONS = ['0', '5', '12', '18', '28'];

const pct = (n) => `${Number(n ?? 0)}%`;

const emptyForm = {
  taxCode: '',
  taxType: 'GOODS',
  description: '',
  gstPercentage: '',
};

const TaxConfigurationManagement = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Toolbar search + filters
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');   // '' | 'GOODS' | 'SERVICE'
  const [gstFilter, setGstFilter] = useState('');      // '' | '0'|'5'|...
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'true' | 'false'

  // Add / Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // the row being edited, or null for Add
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [togglingId, setTogglingId] = useState(null);

  // Server-side pagination — the backend caps its page size, so the UI must page
  // through rather than pull a single oversized list (an HSN/SAC directory can
  // easily exceed a page).
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await taxMasterService.list({
        page,
        limit: PAGE_SIZE,
        search: searchTerm.trim() || undefined,
        taxType: typeFilter || undefined,
        gstPercentage: gstFilter || undefined,
        isActive: statusFilter || undefined,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      setTotalPages(res?.pagination?.totalPages || 1);
      setTotal(res?.pagination?.total ?? 0);
    } catch (err) {
      console.error('Failed to load tax configurations:', err);
      setError(err?.response?.data?.message || 'Failed to load tax configurations');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, typeFilter, gstFilter, statusFilter]);

  // All filters (search, type, GST%, status) are applied server-side so they
  // work across the whole dataset, not just the current page. Debounced so a
  // fast typist doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(loadRows, 300);
    return () => clearTimeout(t);
  }, [loadRows]);

  // Any filter change resets to page 1 — otherwise a narrower result set could
  // leave you stranded on a now-out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [searchTerm, typeFilter, gstFilter, statusFilter]);

  const visibleRows = rows;

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      taxCode: row.taxCode ?? '',
      taxType: row.taxType ?? 'GOODS',
      description: row.description ?? '',
      gstPercentage: String(row.gstPercentage ?? ''),
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditing(null);
    setForm(emptyForm);
    setFormError('');
  };

  const validate = () => {
    const codeLabel = codeLabelFor(form.taxType);
    if (!form.taxCode.trim()) return `${codeLabel} is required.`;
    if (form.taxCode.trim().length > 20) return `${codeLabel} cannot exceed 20 characters.`;
    if (!form.taxType) return 'Tax Type is required.';
    const gst = Number(form.gstPercentage);
    if (form.gstPercentage === '' || Number.isNaN(gst)) return 'GST Percentage is required.';
    if (gst < 0 || gst > 100) return 'GST Percentage must be between 0 and 100.';
    if (form.description && form.description.length > 255) return 'Description cannot exceed 255 characters.';
    return '';
  };

  const handleSave = async () => {
    const msg = validate();
    if (msg) { setFormError(msg); return; }
    setSaving(true);
    setFormError('');
    try {
      if (editing) {
        await taxMasterService.update(editing.id, {
          taxCode: form.taxCode,
          taxType: form.taxType,
          description: form.description,
          gstPercentage: form.gstPercentage,
        });
        toast.success('Tax configuration updated');
      } else {
        await taxMasterService.create({
          taxCode: form.taxCode,
          taxType: form.taxType,
          description: form.description,
          gstPercentage: form.gstPercentage,
        });
        toast.success('Tax configuration created');
      }
      setShowModal(false);
      setEditing(null);
      setForm(emptyForm);
      await loadRows();
    } catch (err) {
      console.error('Failed to save tax configuration:', err);
      const apiMsg = err?.response?.data?.message;
      setFormError(Array.isArray(apiMsg) ? apiMsg.join(', ') : (apiMsg || 'Failed to save tax configuration'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (row) => {
    setTogglingId(row.id);
    try {
      await taxMasterService.setActive(row.id, !row.isActive);
      toast.success(row.isActive ? 'Tax configuration deactivated' : 'Tax configuration activated');
      await loadRows();
    } catch (err) {
      console.error('Failed to toggle status:', err);
      toast.error(err?.response?.data?.message || 'Failed to update status');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Tax Configuration</h2>
          <p className="text-gray-600 mt-1">Manage your products tax codes, HSN directory and GST breakdowns</p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 bg-[#548B2E] text-white px-4 py-2 rounded-lg hover:bg-[#3f6b20] transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Tax Configuration
        </button>
      </div>

      {/* Toolbar: search + filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search tax configurations..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]/40"
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#548B2E]/40"
          >
            <option value="">Tax Type</option>
            {TAX_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <select
            value={gstFilter}
            onChange={(e) => setGstFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#548B2E]/40"
          >
            <option value="">GST Percentage</option>
            {GST_FILTER_OPTIONS.map((g) => (
              <option key={g} value={g}>{g}%</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#548B2E]/40"
          >
            <option value="">Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                <th className="text-left font-semibold px-4 py-3">HSN / SAC Code</th>
                <th className="text-left font-semibold px-4 py-3">Tax Type</th>
                <th className="text-left font-semibold px-4 py-3">GST %</th>
                <th className="text-left font-semibold px-4 py-3">CGST</th>
                <th className="text-left font-semibold px-4 py-3">SGST</th>
                <th className="text-left font-semibold px-4 py-3">IGST</th>
                <th className="text-left font-semibold px-4 py-3">Status</th>
                <th className="text-right font-semibold px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                    Loading tax configurations...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-red-500">{error}</td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                    <Receipt className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    No tax configurations found.
                  </td>
                </tr>
              ) : (
                visibleRows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-800">{row.taxCode}</td>
                    <td className="px-4 py-3 text-gray-600">{TAX_TYPE_LABELS[row.taxType] || row.taxType}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{pct(row.gstPercentage)}</td>
                    <td className="px-4 py-3 text-gray-600">{pct(row.cgstPercentage)}</td>
                    <td className="px-4 py-3 text-gray-600">{pct(row.sgstPercentage)}</td>
                    <td className="px-4 py-3 text-gray-600">{pct(row.igstPercentage)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${row.isActive ? 'text-green-600' : 'text-red-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${row.isActive ? 'bg-green-500' : 'bg-red-500'}`} />
                        {row.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(row)}
                          title="Edit"
                          className="p-2 rounded-lg text-gray-500 hover:text-[#548B2E] hover:bg-[#548B2E]/10 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(row)}
                          disabled={togglingId === row.id}
                          title={row.isActive ? 'Deactivate' : 'Activate'}
                          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors disabled:opacity-50"
                        >
                          {togglingId === row.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : row.isActive ? (
                            <ToggleRight className="w-5 h-5 text-green-600" />
                          ) : (
                            <ToggleLeft className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer — only when there's more than one page. */}
        {!loading && !error && totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 text-sm">
            <span className="text-gray-500">
              Page {page} of {totalPages} · {total} total
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40" onClick={closeModal}>
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-start justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-800">
                  {editing ? 'Edit Tax Configuration' : 'Add Tax Configuration'}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {editing ? 'Update the tax bracket or code description' : 'Set up a new GST bracket or tax-exempt code'}
                </p>
              </div>
              <button onClick={closeModal} className="p-1 text-gray-400 hover:text-gray-600" disabled={saving}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal body */}
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {codeLabelFor(form.taxType)} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.taxCode}
                  onChange={(e) => setForm((f) => ({ ...f, taxCode: e.target.value }))}
                  maxLength={20}
                  placeholder={form.taxType === 'SERVICE' ? 'e.g., 9971' : 'e.g., 0901'}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]/40"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tax Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.taxType}
                  onChange={(e) => setForm((f) => ({ ...f, taxType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#548B2E]/40"
                >
                  {TAX_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GST Percentage (%) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.gstPercentage}
                  onChange={(e) => setForm((f) => ({ ...f, gstPercentage: e.target.value }))}
                  placeholder="e.g., 18"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]/40"
                />
                <p className="text-xs text-gray-400 mt-1">
                  CGST, SGST and IGST are derived automatically (CGST = SGST = GST/2, IGST = GST).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  maxLength={255}
                  rows={3}
                  placeholder="Enter any specific notes about this configuration..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]/40 resize-none"
                />
              </div>

              {formError && (
                <p className="text-sm text-red-500">{formError}</p>
              )}
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100">
              <button
                onClick={closeModal}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 bg-[#548B2E] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#3f6b20] transition-colors disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editing ? 'Update Configuration' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

TaxConfigurationManagement.propTypes = {};

export default TaxConfigurationManagement;
